import { useEffect, useRef } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";

import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { log } from "@/lib/logger";

import { AppErrorBoundary } from "./components/common/error-boundary";
import { NotFoundPage } from "./components/common/not-found";
import { AdminLayout, AdminGuard } from "./routes/admin";
import { AdminDoctorsRoute } from "./routes/admin.doctors";
import { AdminDashboardRoute } from "./routes/admin.index";
import { AdminLoginRoute } from "./routes/admin.login";
import { BookingsRoute } from "./routes/bookings";
import { VideoCallRoute } from "./routes/bookings.call";
import { DashboardRoute } from "./routes/dashboard";
import { WelcomeRoute } from "./routes/index";
import { LoginRoute } from "./routes/login";
import { ProfileRoute } from "./routes/profile";
import { RegisterRoute } from "./routes/register";
import { SettingsRoute } from "./routes/settings";
import { SignupRoute } from "./routes/signup";

/** Navigation is the coarsest "what is the user doing" signal there is. */
function NavigationLogger() {
  const { pathname } = useLocation();
  // StrictMode mounts, unmounts and remounts effects in development, which
  // would log every navigation twice. Track what was last recorded instead.
  const lastLogged = useRef<string | null>(null);

  useEffect(() => {
    if (lastLogged.current === pathname) return;
    lastLogged.current = pathname;
    log.info("app", "navigate", { to: pathname });
  }, [pathname]);

  return null;
}

export function App() {
  return (
    <AppErrorBoundary>
      <AuthProvider>
        <NavigationLogger />
        <Routes>
          <Route path="/" element={<WelcomeRoute />} />
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/signup" element={<SignupRoute />} />
          <Route path="/register" element={<RegisterRoute />} />

          <Route path="/dashboard" element={<DashboardRoute />} />
          <Route path="/bookings" element={<BookingsRoute />} />
          <Route path="/bookings/:bookingId/call" element={<VideoCallRoute />} />
          <Route path="/profile" element={<ProfileRoute />} />
          <Route path="/settings" element={<SettingsRoute />} />

          {/* The admin portal is a separate shell with its own secret-based gate. */}
          <Route path="/admin/login" element={<AdminLayout />}>
            <Route index element={<AdminLoginRoute />} />
          </Route>
          <Route
            path="/admin"
            element={
              <AdminGuard>
                <AdminLayout />
              </AdminGuard>
            }
          >
            <Route index element={<AdminDashboardRoute />} />
            <Route path="doctors" element={<AdminDoctorsRoute />} />
          </Route>

          {/* Trailing-slash and legacy paths land somewhere sensible. */}
          <Route path="/admin/*" element={<Navigate to="/admin" replace />} />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Toaster />
      </AuthProvider>
    </AppErrorBoundary>
  );
}
